import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { AuthGuard } from "./components/AuthGuard";
import { Layout } from "./components/Layout";
import { CampaignPage } from "./pages/CampaignPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { PipelineBoardPage } from "./pages/PipelineBoardPage";
import { PipelinesPage } from "./pages/PipelinesPage";
import { ContactDetailPage } from "./pages/ContactDetailPage";
import { ContactsPage } from "./pages/ContactsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { TasksPage } from "./pages/TasksPage";
import { TeamLayout } from "./pages/team/TeamLayout";
import { TeamOverviewPage } from "./pages/team/TeamOverviewPage";
import { QuestionsPage } from "./pages/team/QuestionsPage";
import { ReviewsPage } from "./pages/team/ReviewsPage";
import { ReviewDetailPage } from "./pages/team/ReviewDetailPage";
import { EmailDraftsPage } from "./pages/team/EmailDraftsPage";
import { BriefingsPage } from "./pages/team/BriefingsPage";
import { ReportsPage } from "./pages/team/ReportsPage";
import { EmailTrustPage } from "./pages/team/EmailTrustPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AuditPage } from "./pages/admin/AuditPage";
import { ConfirmationsPage } from "./pages/admin/ConfirmationsPage";
import { CronJobsPage } from "./pages/admin/CronJobsPage";
import { DenialsPage } from "./pages/admin/DenialsPage";
import { HealthPage } from "./pages/admin/HealthPage";
import { RolesPage } from "./pages/admin/RolesPage";
import { ScopesPage } from "./pages/admin/ScopesPage";
import { SessionsPage } from "./pages/admin/SessionsPage";
import { ToolsPage } from "./pages/admin/ToolsPage";
import { UsersPage } from "./pages/admin/UsersPage";
import { LeadSourcesPage } from "./pages/LeadSourcesPage";
import { FocusPage } from "./pages/FocusPage";
import { GoPage } from "./pages/GoPage";
import { b2gRedirectTarget } from "./lib/console";

/**
 * Redirect that keeps the query string. The old /admin/ai-team URLs carry
 * drawer state (`?agent=…&tab=…&job=…`) in the search params — a plain
 * `<Navigate>` would land bookmarked deep links on an empty page.
 */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** The old B2G page is the `market-access` campaign on the console now. */
function B2GRedirect() {
  const { search } = useLocation();
  return <Navigate to={b2gRedirectTarget(search)} replace />;
}

/** A stable link to one task: the Tasks page with its drawer open. */
function TaskRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/tasks?task=${encodeURIComponent(id ?? "")}`} replace />;
}

function LegacyReviewRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/team/reviews/${id}${search}`} replace />;
}

export default function App() {
  return (
    <AuthGuard>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="/focus" element={<FocusPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/:id" element={<ContactDetailPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/:slug" element={<CampaignPage />} />
          <Route path="/pipelines" element={<PipelinesPage />} />
          <Route path="/pipelines/:id" element={<PipelineBoardPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/:id" element={<TaskRedirect />} />
          <Route path="/lead-sources" element={<LeadSourcesPage />} />
          <Route path="/b2g" element={<B2GRedirect />} />
          <Route path="/go/:id" element={<GoPage />} />
          <Route path="/team" element={<TeamLayout />}>
            <Route index element={<TeamOverviewPage />} />
            <Route path="questions" element={<QuestionsPage />} />
            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="reviews/:id" element={<ReviewDetailPage />} />
            <Route path="email-drafts" element={<EmailDraftsPage />} />
            <Route path="email-drafts/:id" element={<ReviewDetailPage />} />
            <Route path="briefings" element={<BriefingsPage />} />
            <Route path="briefings/:id" element={<ReviewDetailPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="email-trust" element={<EmailTrustPage />} />
          </Route>
          {/* Legacy /admin locations of the team pages. */}
          <Route
            path="/admin/ai-team"
            element={<LegacyRedirect to="/team" />}
          />
          <Route
            path="/admin/ai-team/questions"
            element={<LegacyRedirect to="/team/questions" />}
          />
          <Route
            path="/admin/ai-team/reviews"
            element={<LegacyRedirect to="/team/reviews" />}
          />
          <Route
            path="/admin/ai-team/reviews/:id"
            element={<LegacyReviewRedirect />}
          />
          <Route
            path="/admin/ai-team/reports"
            element={<LegacyRedirect to="/team/reports" />}
          />
          <Route
            path="/admin/email-trust"
            element={<LegacyRedirect to="/team/email-trust" />}
          />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="tools" element={<ToolsPage />} />
            <Route path="cron-jobs" element={<CronJobsPage />} />
            <Route path="scopes" element={<ScopesPage />} />
            <Route path="confirmations" element={<ConfirmationsPage />} />
            <Route path="sessions" element={<SessionsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="denials" element={<DenialsPage />} />
            <Route path="health" element={<HealthPage />} />
          </Route>
        </Route>
      </Routes>
    </AuthGuard>
  );
}
